/**
 * A Navigator LockManager lock for Supabase auth that bounds how long acquisition waits.
 *
 * ⚠️ WHY THIS EXISTS. Measured 2026-08-16 against the running app:
 *
 *     LOCKS: held [{ name: "lock:solerro-pm-auth", mode: "exclusive" }]
 *     acquire same lock -> BLOCKED (lock held)
 *     in-page getSession(): NEVER SETTLED
 *     BODY: "Loading..."
 *
 * `@supabase/auth-js` wraps its auth operations in a Web Lock named
 * `lock:<storageKey>`. `getSession()` first awaits `initializePromise`, which is created
 * as `_acquireLock(-1, () => this._initialize())` — and **-1 means wait forever**. If a
 * request inside `_initialize()` is aborted (which happens routinely when the app
 * navigates while auth is in flight) the lock can be left held. Every later
 * `getSession()` then waits on it indefinitely.
 *
 * The user-visible result is severe and permanent: `App.tsx` awaits `getSession()` inside
 * `checkUserStatus`, so its `finally { setIsLoading(false) }` never runs and the whole
 * application sits on "Loading..." until the tab is manually reloaded. Navigating straight
 * after signing in is enough to trigger it.
 *
 * ⚠️ THE TRADE-OFF, STATED PLAINLY. The lock exists to stop two tabs refreshing the same
 * token at once. When acquisition times out here we run the operation ANYWAY, unlocked.
 * That reintroduces a small window for a concurrent refresh, which Supabase's refresh-token
 * rotation tolerates via its reuse grace period. The timeout is long enough that it is only
 * reached when the lock is genuinely stuck rather than merely busy.
 *
 * ⚠️ WHAT THIS DOES AND DOES NOT BUY YOU. Both halves were measured, not reasoned about,
 * on 2026-08-16. Do not upgrade the first claim into the second.
 *
 *   IT FIXES THE OBSERVED BUG. Sign in, then navigate immediately, repeated against the
 *   running app: without this lock 2 of 3 runs wedged on "Loading..." permanently, with the
 *   in-page `getSession()` never settling. With it, 5 of 5 runs rendered the page in 2-4s,
 *   `navigator.locks.query()` came back empty and `getSession()` resolved in ~0ms. The
 *   aborted `/auth/v1/user` request that triggers the wedge was present in all 8 runs, so
 *   the trigger is not what changed. Notably the timeout below never fired in any of those
 *   runs, so whatever it repairs, it is not repaired by the unlocked-fallback path.
 *
 *   IT DOES NOT MAKE THE APP IMMUNE TO A STUCK LOCK. Hold `lock:solerro-pm-auth` from an
 *   init script and never release it, then load the app: the timeout fires once at 5s, that
 *   one operation runs unlocked, and the app still sits on "Loading..." for the full 60s
 *   the probe watched. Something after the first acquisition stays blocked. So this bounds
 *   a CONTENDED lock, not a permanently held one.
 *
 * The guarantee that "the app cannot hang" therefore does not live here and cannot: it
 * belongs at the call sites. That is now done — AuthContext, ProtectedRouteWithResetCheck and
 * MainLayout each give up on a bound and the last shows a recovery screen. See sessionBoot.ts.
 *
 * What this file owns is making a stuck lock CHEAP as well as non-fatal: see
 * AUTH_LOCK_STUCK_COOLDOWN_MS. Bounding the wait alone turned an infinite hang into a 5s toll
 * on every auth operation, which on a machine with a wedged tab reads as "really slow" plus a
 * console full of warnings. Both were reported from a real machine before that was fixed.
 */

/** How long to wait for the auth lock before proceeding without it. */
export const AUTH_LOCK_ACQUIRE_TIMEOUT_MS = 5_000;

/**
 * How long to stop queueing for a lock that has already proved itself stuck.
 *
 * ⚠️ WITHOUT THIS THE BOUND IS A TOLL, NOT A FIX. Reported from a real machine 2026-08-17:
 * the console full of `auth lock ... was not released within 5000ms` and the app "really
 * slow". Those are one fact, not two. Web Locks are shared across every TAB of an origin, so
 * a single wedged tab holds `lock:solerro-pm-auth` for the whole browser, and EVERY auth
 * operation in every other tab then paid the full 5s before proceeding. A boot makes many.
 *
 * So the first timeout records that the lock is stuck and the next 30 seconds of operations
 * skip acquisition entirely — same unlocked outcome, without the wait or the log spam. It
 * expires rather than latching, because the tab holding the lock will eventually close and
 * locking should resume when it does.
 */
export const AUTH_LOCK_STUCK_COOLDOWN_MS = 30_000;

/** When the lock was last observed stuck, by lock name. Module scope: one per document. */
const stuckUntilByName = new Map<string, number>();

/** Forget every recorded stuck lock. For tests; the app has no reason to call it. */
export function resetAuthLockCircuit(): void {
  stuckUntilByName.clear();
}

/** The subset of the Web Locks API this needs, so it can be tested without a browser. */
export interface LockManagerLike {
  request(
    name: string,
    options: { mode?: 'exclusive' | 'shared'; ifAvailable?: boolean; signal?: AbortSignal },
    callback: (lock: unknown) => Promise<unknown>
  ): Promise<unknown>;
}

export interface BoundedAuthLockOptions {
  timeoutMs?: number;
  /** How long to skip a lock already known to be stuck. */
  cooldownMs?: number;
  locks?: LockManagerLike | undefined;
  /** Called when the lock could not be acquired and the operation ran unlocked. */
  onTimeout?: (name: string, timeoutMs: number) => void;
}

/**
 * The failure auth-js expects when `acquireTimeout === 0` and the lock is taken.
 *
 * ⚠️ `isAcquireTimeout` is load-bearing, not decoration. `_autoRefreshTokenTick` calls
 * `_acquireLock(0, ...)` and its catch re-throws anything that is not
 * `e.isAcquireTimeout || e instanceof LockAcquireTimeoutError`. The tick is scheduled as
 * `setInterval(() => this._autoRefreshTokenTick(), ...)`, which discards the promise, so an
 * unmarked error becomes an unhandled rejection every 30 seconds that the lock is busy.
 * auth-js's `NavigatorLockAcquireTimeoutError` is not exported, so we set the property it is
 * detected by and keep its message wording.
 */
function acquireTimeoutError(name: string): Error & { isAcquireTimeout: true } {
  return Object.assign(
    new Error(`Acquiring an exclusive Navigator LockManager lock "${name}" immediately failed`),
    { isAcquireTimeout: true as const }
  );
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * Build the `lock` function that `createClient({ auth: { lock } })` expects.
 *
 * Semantics deliberately match `auth-js`'s own `navigatorLock` except for the unbounded
 * case:
 *   acquireTimeout === 0  -> try once, do not wait (auth-js relies on this failing loudly)
 *   acquireTimeout  <  0  -> auth-js means "wait forever"; we wait `timeoutMs`, then run
 *                            the operation unlocked rather than hang
 *   acquireTimeout  >  0  -> honour it, capped at `timeoutMs`
 */
export function createBoundedAuthLock(options: BoundedAuthLockOptions = {}) {
  const timeoutMs = options.timeoutMs ?? AUTH_LOCK_ACQUIRE_TIMEOUT_MS;
  const cooldownMs = options.cooldownMs ?? AUTH_LOCK_STUCK_COOLDOWN_MS;

  return async function boundedAuthLock<R>(
    name: string,
    acquireTimeout: number,
    fn: () => Promise<R>
  ): Promise<R> {
    const locks =
      options.locks ??
      (typeof navigator !== 'undefined'
        ? (navigator as Navigator & { locks?: LockManagerLike }).locks
        : undefined);

    // No LockManager (older browsers, SSR, tests): auth-js's own fallback is to run
    // unlocked, so do the same rather than inventing different behaviour.
    if (!locks) {
      return await fn();
    }

    if (acquireTimeout === 0) {
      return (await locks.request(name, { mode: 'exclusive', ifAvailable: true }, async (lock) => {
        if (!lock) {
          throw acquireTimeoutError(name);
        }
        return await fn();
      })) as R;
    }

    // Already known stuck: do not queue behind it again, just do the work. Deliberately
    // AFTER the acquireTimeout === 0 branch above, which auth-js needs to keep failing.
    const stuckUntil = stuckUntilByName.get(name);
    if (stuckUntil !== undefined && Date.now() < stuckUntil) {
      return await fn();
    }

    const wait = acquireTimeout < 0 ? timeoutMs : Math.min(acquireTimeout, timeoutMs);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), wait);

    try {
      return (await locks.request(
        name,
        { mode: 'exclusive', signal: controller.signal },
        async () => await fn()
      )) as R;
    } catch (error) {
      // The signal only cancels a PENDING request; once the lock is granted an abort has
      // no effect. So `signal.aborted` plus an AbortError reliably means we never got the
      // lock, and cannot mean that `fn` itself threw.
      if (controller.signal.aborted && isAbortError(error)) {
        stuckUntilByName.set(name, Date.now() + cooldownMs);
        options.onTimeout?.(name, wait);
        return await fn();
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  };
}
